import type { TMedia } from '~constants/media';
import { saveToSessionStorage, ssKeys } from '~features/storage';
import { api, apiTags } from '~shared/api/origin';
import { TokenManager } from '~shared/services/ClientTokenManager';

import type {
	ActivateGeneratedBotApiResponse,
	CheckGenerationReferenceApiResponse,
	CreateGenerationReferenceApiResponse,
	GenerateBotApiRequest,
	GenerateBotApiResponse,
	GeneratedBotShort,
	GeneratedMedia,
	GenerateImagesApiRequest,
	GenerateImagesApiResponse,
	GenerateMediaApiResponse,
	GenerateVideosApiRequest,
	GenerateVideosApiResponse,
	GetGenerationBotQuotaApiResponse,
	GetGenerationImagesQuotaApiResponse,
	GetGenerationImagesSettingsApiResponse,
	GetGenerationMediaBotsApiResponse,
	GetGenerationSettingsApiResponse,
	GetGenerationVideosQuotaApiResponse,
	GetGenerationVideosSettingsApiResponse,
	UploadGenerationReferenceApiResponse,
} from './types';

export function prepareMediaItem(item: GeneratedMedia) {
	const { imageLinks, mediaLinks, type, ...rest } = item as GeneratedMedia & {
		imageLinks?: unknown;
	};

	return {
		type: type || (imageLinks ? 'image' : 'video'),
		mediaLinks: imageLinks || mediaLinks,
		...rest,
	} as GeneratedMedia;
}

export function prepareMedia(media: GeneratedMedia[]) {
	return media.map(prepareMediaItem);
}

export const {
	util,
	// character
	useGetGenerationSettingsQuery,
	useGetGenerationBotQuotaQuery,
	useGenerateBotMutation,
	useActivateGeneratedBotMutation,
	useCreateGenerationReferenceMutation,
	useUploadGenerationReferenceMutation,
	useCheckGenerationReferenceMutation,
	// media
	useGetGeneratedMediaQuery,
	useImprovePromptMutation,
	useDeleteGeneratedMediaMutation,
	// images
	useGetGenerationImagesSettingsQuery,
	useGetGenerationImagesBotsQuery,
	useGetGenerationImagesQuotaQuery,
	useGenerateImagesMutation,
	// videos
	useGetGenerationVideosSettingsQuery,
	useGetGenerationVideosBotsQuery,
	useGetGenerationVideosQuotaQuery,
	useGenerateVideosMutation,
	// query state
	endpoints: {
		getGeneratedMedia: { useQueryState: useGetGeneratedMediaQueryState },
	},
} = api.injectEndpoints({
	endpoints: (build) => ({
		// character
		getGenerationSettings: build.query<GetGenerationSettingsApiResponse, void>({
			query: () => ({
				url: 'ai/configuration/gtool/v1/settings',
			}),
		}),

		getGenerationBotQuota: build.query<
			GetGenerationBotQuotaApiResponse,
			{ userId: string }
		>({
			query: ({ userId }) => ({
				url: `ai/generation/${userId}/bot/quota	`,
			}),
			providesTags: [apiTags.characterGenerationQuota],
		}),

		generateBot: build.mutation<
			GeneratedBotShort,
			{ userId: string; body: GenerateBotApiRequest }
		>({
			query: ({ userId, body }) => ({
				url: `ai/generation/${userId}/bot`,
				method: 'PUT',
				body,
			}),
			transformResponse: (data: GenerateBotApiResponse): GeneratedBotShort => {
				const { id, status, info } = data.created;

				return {
					id,
					status,
					name: info.name,
					webBackgroundPath: info.webBackgroundPath,
					webBackgroundLinks: info.webBackgroundLinks,
				};
			},
		}),

		activateGeneratedBot: build.mutation<
			ActivateGeneratedBotApiResponse,
			{ userId: string; botId: number; isPublic: boolean }
		>({
			query: ({ userId, botId, isPublic }) => ({
				url: `ai/generation/${userId}/bot/${botId}/activate`,
				method: 'POST',
				body: {
					access: isPublic ? 'public' : 'private',
				},
			}),
			async onQueryStarted({ isPublic }, { queryFulfilled, dispatch }) {
				try {
					await queryFulfilled;
					if (isPublic) {
						dispatch(api.util.invalidateTags([apiTags.grid]));
					}
				} catch {}
			},
		}),

		createGenerationReference: build.mutation<
			CreateGenerationReferenceApiResponse,
			{ userId: string }
		>({
			query: ({ userId }) => ({
				url: `ai/generation/${userId}/reference/create`,
				method: 'POST',
			}),
		}),

		uploadGenerationReference: build.mutation<
			UploadGenerationReferenceApiResponse,
			{ url: string; body: FormData }
		>({
			query: ({ url, body }) => ({
				url,
				method: 'POST',
				body,
			}),
		}),

		checkGenerationReference: build.mutation<
			TMedia[],
			{ userId: string; referenceId: number }
		>({
			query: ({ userId, referenceId }) => ({
				url: `ai/generation/${userId}/reference/${referenceId}/check`,
				method: 'POST',
			}),
			transformResponse: (
				data: CheckGenerationReferenceApiResponse
			): TMedia[] => {
				return data.links;
			},
		}),

		// images
		getGenerationImagesSettings: build.query<
			GetGenerationImagesSettingsApiResponse,
			{ userId: string }
		>({
			query: ({ userId }) => ({
				url: `ai/generation/${userId}/images/settings`,
			}),
		}),

		getGenerationImagesBots: build.query<
			GetGenerationMediaBotsApiResponse['bots'],
			{ userId: string }
		>({
			query: ({ userId }) => ({
				url: `ai/generation/${userId}/images/bots`,
			}),
			transformResponse: (
				data: GetGenerationMediaBotsApiResponse
			): GetGenerationMediaBotsApiResponse['bots'] => {
				return data.bots;
			},
			providesTags: [apiTags.imagesGenerationBots],
		}),

		getGenerationImagesQuota: build.query<
			GetGenerationImagesQuotaApiResponse,
			{ userId: string }
		>({
			query: ({ userId }) => ({
				url: `ai/generation/${userId}/images/quota`,
			}),
			providesTags: [apiTags.imagesGenerationQuota],
		}),

		generateImages: build.mutation<
			GenerateImagesApiResponse,
			{ userId: string; body: GenerateImagesApiRequest }
		>({
			query: ({ userId, body }) => ({
				url: `ai/generation/${userId}/image`,
				method: 'PUT',
				body,
			}),
			async onQueryStarted({ userId, body }, { dispatch, queryFulfilled }) {
				saveToSessionStorage(ssKeys.G_TOOL_GENERATE_IN_PROGRESS, true);
				dispatch(
					util.updateQueryData(
						'getGenerationImagesQuota',
						{ userId },
						(draft) => {
							const { type } = TokenManager.getInfo();
							if (!body.botId) {
								if (type === 'demo') {
									draft.quotas.free =
										body.imageCount > draft.quotas.demo
											? 0
											: draft.quotas.demo - body.imageCount;
								}
								if (draft.quotas.free) {
									draft.quotas.free =
										body.imageCount > draft.quotas.free
											? 0
											: draft.quotas.free - body.imageCount;
								} else {
									draft.quotas.monthly =
										body.imageCount > draft.quotas.monthly
											? 0
											: draft.quotas.monthly - body.imageCount;
								}
							}
						}
					)
				);

				try {
					const { data: res } = await queryFulfilled;
					dispatch(
						util.updateQueryData(
							'getGeneratedMedia',
							{ userId, omit: NaN, select: NaN },
							(draft) => {
								const images = prepareMedia(
									res.images.filter(
										({ id }) => !draft.media.find((it) => it.id === id)
									)
								);

								draft.media.unshift(...images);
								draft.totalLength += images.length;
							}
						)
					);
				} catch {}
			},
			invalidatesTags: [apiTags.imagesGenerationQuota],
		}),

		getGeneratedMedia: build.query<
			GenerateMediaApiResponse,
			{ userId: string; omit: number; select: number }
		>({
			query: ({ userId, omit, select }) => ({
				url: `ai/generation/${userId}/media`,
				params: { omit, select },
			}),
			transformResponse: (response: GenerateMediaApiResponse | null) => {
				if (response === null) {
					return {
						receivedCount: 0,
						totalLength: 0,
						media: [],
					};
				}

				const media = prepareMedia(response.media);

				return {
					media,
					receivedCount: media.length,
					totalLength: media.length,
				};
			},
			serializeQueryArgs: ({ endpointName, queryArgs }) => {
				return `${endpointName}-${queryArgs?.userId}`;
			},
			forceRefetch: ({ currentArg, endpointState }) => {
				return (
					// @ts-ignore
					(currentArg?.omit ?? 0) >= (endpointState?.data?.totalLength ?? 0)
				);
			},
			merge: (currentState, incomingState) => {
				currentState.receivedCount = incomingState.receivedCount;
				currentState.totalLength =
					currentState.totalLength + incomingState.receivedCount;

				incomingState.media.forEach((item) => {
					const existedIndex = currentState.media.findIndex(
						({ id }) => id === item.id
					);
					if (existedIndex > -1) {
						currentState.media[existedIndex] = item;
						currentState.totalLength -= 1;
					} else {
						currentState.media.push(item);
					}
				});
			},
			providesTags: [apiTags.generatedMedia],
		}),

		deleteGeneratedMedia: build.mutation<
			undefined,
			{ userId: string; media: GeneratedMedia }
		>({
			query: ({ userId, media }) => ({
				url: `ai/generation/${userId}/${media.type}/${media.id}`,
				method: 'DELETE',
			}),
		}),

		improvePrompt: build.mutation<
			{ prompt: string },
			{ prompt: string; userId: string }
		>({
			query: ({ prompt, userId }) => ({
				url: `ai/generation/${userId}/prompt/improve`,
				method: 'POST',
				body: { prompt },
			}),
		}),

		// videos
		getGenerationVideosSettings: build.query<
			GetGenerationVideosSettingsApiResponse,
			{ userId: string }
		>({
			query: ({ userId }) => ({
				url: `ai/generation/${userId}/videos/settings`,
			}),
		}),

		getGenerationVideosQuota: build.query<
			GetGenerationVideosQuotaApiResponse,
			{ userId: string }
		>({
			query: ({ userId }) => ({
				url: `ai/generation/${userId}/videos/quota`,
			}),
			providesTags: [apiTags.videosGenerationQuota],
		}),

		getGenerationVideosBots: build.query<
			GetGenerationMediaBotsApiResponse['bots'],
			{ userId: string }
		>({
			query: ({ userId }) => ({
				url: `ai/generation/${userId}/videos/bots`,
			}),
			transformResponse: (
				data: GetGenerationMediaBotsApiResponse
			): GetGenerationMediaBotsApiResponse['bots'] => {
				return data.bots;
			},
			providesTags: [apiTags.videosGenerationBots],
		}),

		generateVideos: build.mutation<
			GenerateVideosApiResponse,
			{ userId: string; body: GenerateVideosApiRequest }
		>({
			query: ({ userId, body }) => ({
				url: `ai/generation/${userId}/video`,
				method: 'PUT',
				body,
			}),
			async onQueryStarted({ userId, body }, { dispatch, queryFulfilled }) {
				saveToSessionStorage(ssKeys.G_TOOL_GENERATE_IN_PROGRESS, true);
				dispatch(
					util.updateQueryData(
						'getGenerationVideosQuota',
						{ userId },
						(draft) => {
							if (!body.botId) {
								draft.quotas.monthly = Math.max(0, draft.quotas.monthly - 1);
							}
						}
					)
				);

				try {
					const { data: res } = await queryFulfilled;
					dispatch(
						util.updateQueryData(
							'getGeneratedMedia',
							{ userId, omit: NaN, select: NaN },
							(draft) => {
								const videos = prepareMedia(
									res.videos.filter(
										({ id }) => !draft.media.find((it) => it.id === id)
									)
								);

								draft.media.unshift(...videos);
								draft.totalLength += videos.length;
							}
						)
					);
				} catch {}
			},
			invalidatesTags: [apiTags.videosGenerationQuota],
		}),
	}),
});
